import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, type BillingMe } from "@/lib/api";
import { DOLLAR_IMG } from "@/lib/dollar";
import { goToStripe, mxn } from "@/lib/billing";

export default function SubscribePage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const auto = params.get("auto") === "1";
  const [b, setB] = useState<BillingMe | null>(null);
  const [busy, setBusy] = useState(auto);
  const [pending, setPending] = useState<string | null>(null);
  const started = useRef(false);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ checkout_url: string }>("/billing/checkout", { plan: "monthly", origin_url: window.location.origin });
      if (!goToStripe(r.data.checkout_url)) { setPending(r.data.checkout_url); setBusy(false); }
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  useEffect(() => { api.get<BillingMe>("/billing/me").then((r) => setB(r.data)).catch(() => undefined); }, []);
  useEffect(() => {
    if (!auto || started.current || user === null || (user && user.billing?.access !== "none")) return;
    started.current = true;
    start();
  }, [auto, user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user && user.billing?.access !== "none") return <Navigate to="/resumen" replace />;
  return (
    <section className="fade-up mx-auto max-w-lg py-12 text-center" data-testid="subscribe-page">
      <img src={DOLLAR_IMG.puppy.full} alt="Dollar" className="mx-auto h-40 w-auto" />
      {busy ? (
        <>
          <Loader2 className="mx-auto mt-6 h-8 w-8 animate-spin text-[#2C4F91]" />
          <h1 className="mt-4 font-display text-2xl font-bold text-[#1B2A4A]" data-testid="subscribe-redirecting">Te estamos llevando al pago seguro…</h1>
        </>
      ) : (
        <>
          <h1 className="mt-6 font-display text-3xl font-bold text-[#1B2A4A]" data-testid="subscribe-title">Te falta un paso</h1>
          <p className="mt-3 text-slate-600">Completa tu suscripción para entrar a Otium: {b ? mxn(b.plans.monthly) : "$300 MXN"} al mes, con cobro automático cada mes. Cancelas cuando quieras.</p>
          <button onClick={start} className="mt-7 inline-flex h-12 items-center justify-center rounded-full bg-[#1B2A4A] px-8 font-semibold text-white transition-colors hover:bg-[#2A3D63]" data-testid="subscribe-complete-button">
            Completar mi suscripción
          </button>
          <p className="mt-4 text-xs text-slate-500">Garantía de 7 días: si Otium no es para ti, te devolvemos el 100% de tu pago.</p>
        </>
      )}
      {pending && (
        <p className="mt-6 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4 text-sm text-amber-900" data-testid="subscribe-open-checkout">
          Tu navegador bloqueó la ventana de pago. <a href={pending} target="_blank" rel="noopener noreferrer" className="font-semibold underline">Abrir el pago de Stripe</a>
        </p>
      )}
    </section>
  );
}
